import {
	buildComposerFollowUpOptions,
	type ComposerFollowUpOption
} from '$lib/components/chat/composerFollowUps';
import type { OnboardingDiscoveryQuestionContinuationV1 } from './onboardingDiscoveryQuestionContinuation';

export type PendingOnboardingComposerFlow = {
	stage: string;
	stateId?: string | null;
	workspaceId?: string | null;
	brandId?: string | null;
	discoveryRunId?: string | null;
	pendingDiscoveryQuestion?: OnboardingDiscoveryQuestionContinuationV1 | null;
};

export type PendingOnboardingComposerOption = ComposerFollowUpOption & {
	questionRef: string;
	value: string;
	ordinal: number;
};

export type PendingOnboardingQuestionComposer = {
	status: 'ready' | 'blocked';
	questionRef: string | null;
	options: PendingOnboardingComposerOption[];
	placeholder: string;
	busy: boolean;
};

const isRef = (value: unknown): value is string =>
	typeof value === 'string' && value.trim().length > 0 && value === value.trim();

// A display projection of the scoped flow, never a replacement answer authority.
// The answer bridge still reloads and claims the exact server question on submission.
export const projectPendingOnboardingQuestionComposer = (
	flow: PendingOnboardingComposerFlow,
	busy = false
): PendingOnboardingQuestionComposer | null => {
	if (flow.stage !== 'awaiting_discovery_question') return null;
	const blocked: PendingOnboardingQuestionComposer = {
		status: 'blocked',
		questionRef: null,
		options: [],
		placeholder: '',
		busy: true
	};
	const question = flow.pendingDiscoveryQuestion;
	if (
		!isRef(flow.stateId) ||
		!isRef(flow.workspaceId) ||
		!isRef(flow.brandId) ||
		!isRef(flow.discoveryRunId) ||
		!question ||
		question.version !== 1 ||
		!isRef(question.questionRef) ||
		!isRef(question.question) ||
		question.runRef !== flow.discoveryRunId ||
		question.workspaceRef !== flow.workspaceId ||
		question.brandRef !== flow.brandId ||
		typeof question.allowCustomAnswer !== 'boolean' ||
		!Array.isArray(question.options)
	)
		return blocked;

	const labels = new Set<string>();
	const values = new Set<string>();
	const options: PendingOnboardingComposerOption[] = [];
	let placeholder = '';
	for (const [index, option] of question.options.entries()) {
		if (
			!option ||
			!isRef(option.label) ||
			!isRef(option.value) ||
			labels.has(option.label) ||
			values.has(option.value)
		)
			return blocked;
		labels.add(option.label);
		values.add(option.value);
		const ordinal = index + 1;
		if (question.allowCustomAnswer && option.value === 'custom') {
			placeholder = `${ordinal}. ${option.label}`;
		} else {
			options.push({
				id: JSON.stringify([flow.stateId, question.runRef, question.questionRef, option.value]),
				questionRef: question.questionRef,
				value: option.value,
				ordinal,
				label: option.label,
				prompt: option.label
			});
		}
	}
	return { status: 'ready', questionRef: question.questionRef, options, placeholder, busy };
};

// A pending (including malformed) typed question must never fall back to old string options.
export const resolveNonQuestionComposerFollowUps = (
	pending: PendingOnboardingQuestionComposer | null,
	followUps: unknown
): ComposerFollowUpOption[] => (pending === null ? buildComposerFollowUpOptions(followUps) : []);
